/* eslint-disable no-use-before-define */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { titleize } from 'underscore.string';
import { map, uniqBy, cloneDeep, assign, omit } from 'lodash';
import { useTranslation, withTranslation } from 'react-i18next';
import { equipmentTypes, equipmentSizes } from 'constants/Equipment';
import { getAllEquipment, bulkAddEquipment } from 'utils/EquipmentWebAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import { Modal } from 'react-bootstrap';
import SessionStore from 'stores/SessionStore';
import CustomerSearchWithPreferences from 'components/site/customer-search-with-preferences';
import { getCarrierPreferencesByType } from 'utils/CarrierPreferencesUtils';
import { getReferenceNumberRequirements } from 'utils/UserServiceAPIUtils';
import List from 'components/v2/list/List';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import PanelSection from 'components/v2/PanelSection';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  getOrderMasterReferenceBookingTimeRequirement,
  mapOrderMasterReferenceBookingTimeRequirement,
} from 'utils/OrderBookingUtils';
import CustomerSearch from '../../site/customer-search';
import Alert from '../../ui/Alert';
import Badge from '../../v2/Badge';
import ErrorAlert from '../../ui/ErrorAlert';
import LoadingCircle from '../../ui/LoadingCircle';
import AddButton from '../../v2/buttons/AddButton';
import { getAuthorizedDrivers } from '../../../utils/CarrierDriverWebAPIUtils';
import CargomaticInput from '../../v2/input';
import CargomaticSelect from '../../v2/select';
import Checkbox from '../../v2/forms/Checkbox';
import DeleteButton from '../../v2/buttons/DeleteButton';
import 'react-datepicker/dist/react-datepicker.css';
import StopForm from '../shipments/stops/StopForm';

const MAX_SHIPMENTS = 50;

const EQUIPMENT_PAGE_SIZE = 25;

const SHIPMENT_TEMPLATE_LIMIT = 350;

function getNewStop(data) {
  return {
    action: 'pickup',
    sequence: 1,
    empty: false,
    off_site: false,
    applyEquipment: true,
    window_date: null,
    location: [{ name: '', address: '' }],
    ...data,
  };
}

function getNewGroup({ origin, destination }) {
  return {
    stops: [
      getNewStop({ sequence: 1, action: 'pickup', location: [origin] }),
      getNewStop({ sequence: 2, action: 'deliver', location: [destination] }),
    ],
    consignee: null,
    hasEquipment: true,
    equipment: '',
    page: 1,
    pages: null,
    selectedEquipment: [],
    count: undefined,
  };
}

const mappedEquipmentTypes = map(equipmentTypes, (name, value) => ({
  name,
  value,
}));

const EquipmentRow = ({
  equipment,
  onRemove,
  onChange,
  onApplyAll,
  requiredReferenceNumbers,
}) => {
  const {
    _id,
    equipment_id,
    _equipmentType,
    _type,
    _equipmentSize,
    masterBillOfLading,
  } = equipment;

  function handleRemove() {
    onRemove(equipment_id);
  }

  function handleEquipmentIdChange(value) {
    onChange(equipment_id, 'equipment_id', value);
  }

  function handleSelectEquipmentType(item) {
    onChange(equipment_id, '_type', item.value);
  }

  function handleSelectEquipmentSize(item) {
    onChange(equipment_id, '_equipmentSize', item);
  }

  function handleMasterBillOfLadingChange(item) {
    onChange(equipment_id, 'masterBillOfLading', item);
  }

  function handleApplyAll() {
    onApplyAll(equipment_id);
  }

  function handleReferenceNumberChange(refNo, item) {
    onChange(equipment_id, refNo.reference_number, item);
  }

  const isNew = !_id;
  const { t } = useTranslation();
  if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
    return (
      <PanelSection>
        <div className="justify-between items-start w-full flex flex-wrap -mx-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 flex-1 px-4">
            <div className="relative flex space-x-1">
              {isNew && (
                <CargomaticInput
                  type="text"
                  label={
                    <div className="flex items-center space-x-1">
                      <span>{t('EQUIPMENT')}</span>
                      <Badge color="brand">New</Badge>
                    </div>
                  }
                  onChange={handleEquipmentIdChange}
                  value={equipment_id}
                />
              )}
              {!isNew && (
                <DescriptionListItem label={t('EQUIPMENT')}>
                  {equipment_id}
                </DescriptionListItem>
              )}
            </div>
            <div className="relative flex space-x-1">
              <DescriptionListItem label={t('EQUIPMENT_TYPE')}>
                <span>{t(_equipmentType)}</span>
              </DescriptionListItem>
            </div>
            <div className="relative flex space-x-1">
              {isNew && (
                <CargomaticSelect
                  items={mappedEquipmentTypes}
                  value={_type}
                  textKey="name"
                  valueKey="value"
                  label={t('EQUIPMENT_TYPE')}
                  showCaption
                  onSelect={handleSelectEquipmentType}
                />
              )}
              {!isNew && (
                <div>
                  <DescriptionListItem label={t('EQUIPMENT_TYPE')}>
                    <span>{t(_type)}</span>
                  </DescriptionListItem>
                </div>
              )}
            </div>
            <div className="relative flex space-x-1">
              {isNew && (
                <CargomaticSelect
                  items={equipmentSizes}
                  value={_equipmentSize}
                  label={t('EQUIPMENT_SIZE')}
                  showCaption
                  onSelect={handleSelectEquipmentSize}
                />
              )}
              {!isNew && (
                <div>
                  <DescriptionListItem label={t('EQUIPMENT_SIZE')}>
                    <span>{t(_equipmentSize)}</span>
                  </DescriptionListItem>
                </div>
              )}
            </div>
            <div className="col-span-full">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {requiredReferenceNumbers.map((refNo) => {
                  return (
                    <CargomaticInput
                      key={refNo.reference_number}
                      label={`${t(refNo?.reference_number?.toUpperCase())} *`}
                      value={equipment[refNo.reference_number] ?? ''}
                      onChange={(val) =>
                        handleReferenceNumberChange(refNo, val)
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2 px-4">
            <DeleteButton
              size="small"
              title={t('REMOVE_WITH_VALUE', { value: equipment_id })}
              onClick={handleRemove}
            />
            {isNew && (
              <button
                className="button button-hollow-green"
                type="button"
                onClick={handleApplyAll}
              >
                {t('APPLY_ALL')}
              </button>
            )}
          </div>
        </div>
      </PanelSection>
    );
  }
  return (
    <tr className="">
      <td>
        <div className="relative flex space-x-1">
          {isNew && (
            <CargomaticInput
              className="w-32"
              hideLabel
              onChange={handleEquipmentIdChange}
              value={equipment_id}
            />
          )}
          {!isNew && <span>{equipment_id}</span>}
          {isNew && <Badge className="" color="brand" text={t('NEW')} />}
        </div>
      </td>
      <td>
        <div className="relative flex space-x-1">
          <span>{titleize(_equipmentType)}</span>
        </div>
      </td>
      <td>
        {isNew && (
          <CargomaticSelect
            items={mappedEquipmentTypes}
            value={_type}
            textKey="name"
            className="w-36"
            valueKey="value"
            hideLabel
            showCaption
            onSelect={handleSelectEquipmentType}
          />
        )}
        {!isNew && <span>{_type}</span>}
      </td>
      <td>
        <div className="w-20">
          {isNew && (
            <CargomaticSelect
              items={equipmentSizes}
              value={_equipmentSize}
              hideLabel
              showCaption
              onSelect={handleSelectEquipmentSize}
            />
          )}
          {!isNew && <span>{_equipmentSize}</span>}
        </div>
      </td>
      <td>
        <CargomaticInput
          value={masterBillOfLading}
          onChange={handleMasterBillOfLadingChange}
          hideLabel
          className="w-36"
        />
      </td>
      <td>
        <div className="flex items-center space-x-2">
          <DeleteButton
            size="small"
            title={t('REMOVE_WITH_VALUE', { value: equipment_id })}
            onClick={handleRemove}
          />
          {isNew && (
            <button
              className="button button-hollow-green"
              type="button"
              onClick={handleApplyAll}
            >
              {t('APPLY_ALL')}
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const Group = ({
  index,
  group,
  onAddStop,
  onFieldChange,
  onAddEquipment,
  onStopChange,
  onConsigneeChange,
  onRemoveConsignee,
  onRemove,
  onRemoveStop,
  canRemove,
  groupIndexPendingEquipment,
  onRemoveEquipment,
  onEquipmentFieldChange,
  onApplyAll,
  requiredReferenceNumbers,
  referenceNumbersWithoutEquipment,
  onReferenceNumberChange,
}) => {
  function handleToggleBookingType(checked) {
    onFieldChange('hasEquipment', checked);
  }

  function handleShipmentCountChange(value) {
    onFieldChange('count', value);
  }

  function handleEquipmentChange(value) {
    onFieldChange('equipment', value);
  }

  function handleStopChange(stop) {
    onStopChange(stop);
  }

  function handleRemoveStop(stop) {
    onRemoveStop(stop);
  }

  const handleConsigneeSelect = (_value, item) => {
    onConsigneeChange({
      consignee_name: item.company_name,
      consignee: item._id,
    });
  };

  const handleConsigneeFieldChange = (value) => {
    onConsigneeChange({
      vendor_name: value,
      vendor: null,
    });
  };

  const handleRemoveConsignee = () => {
    onRemoveConsignee();
  };

  const handleReferenceNumberChanges = (refNo, value) => {
    onReferenceNumberChange(refNo, value);
  };

  const bookingTypeId = `bookingType_${index}`;
  const { t } = useTranslation();
  if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
    return (
      <div className="p-4 shadow rounded border">
        <div className="flex justify-between items-center mb-4 border-b pb-4 -mx-8">
          <div className="flex flex-wrap space-x-1 px-8">
            <h4 className="my-0 flex-1 sm:w-24 w-full">
              {t('GROUP_WITH_VALUE', { value: index + 1 })}
            </h4>
            {!group.consignee && (
              <div className="w-full sm:w-64">
                <label className="mb-1 form-label">
                  {t('SEARCH_CONSIGNEE')}
                </label>
                <CustomerSearch
                  clearOnSelect={false}
                  onChange={handleConsigneeFieldChange}
                  onSelect={handleConsigneeSelect}
                  placeholder={t('SEARCH_FOR_CONSIGNEE')}
                  type="consignees"
                  authorizedOnly={false}
                  value={group.consignee_name}
                />
              </div>
            )}
            {group.consignee && (
              <div>
                <label className="mb-1 form-label">
                  {titleize(t('CONSIGNEE', { count: 1 }))}
                </label>
                <Badge color="blue" onRemove={handleRemoveConsignee} withRemove>
                  {group.consignee_name}
                </Badge>
              </div>
            )}
          </div>
          <div className="px-8">
            {canRemove && (
              <div className="form-actions">
                <DeleteButton onClick={onRemove} />
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 divide-y divide-gray-200">
          {group.stops.map((stop, i) => (
            <StopForm
              key={i}
              stop={stop}
              onStopChange={handleStopChange}
              onRemove={handleRemoveStop}
            />
          ))}
          <a href="#" className="block pt-4 underline" onClick={onAddStop}>
            {t('ADD_STOP')}
          </a>
        </div>
        <div className="mt-6 flex flex-wrap -mx-4">
          <Checkbox
            className="mb-4 px-4"
            name={bookingTypeId}
            id={bookingTypeId}
            checked={group.hasEquipment}
            onChange={handleToggleBookingType}
          >
            {t('HAS_EQUIPMENT')}
          </Checkbox>
          {!group.hasEquipment && (
            <CargomaticInput
              className="px-4 mb-4 w-auto"
              type="number"
              step={1}
              min={1}
              max={MAX_SHIPMENTS}
              label={t('HOW_MANY_SHIPMENTS')}
              value={group.count}
              placeholder={t('NUMBER_OF_SHIPMENTS')}
              onChange={handleShipmentCountChange}
            />
          )}
        </div>
        {group.hasEquipment && (
          <div className="lg:w-1/2 w-full">
            <CargomaticInput
              className="mb-2"
              type="textarea"
              label={titleize(t('EQUIPMENT'))}
              info={t('ADD_EQUIPMENT_NOTE_WITH_ADD_INFO')}
              value={group.equipment}
              placeholder={t('ADD_EQUIPMENT_NOTE')}
              onChange={handleEquipmentChange}
              rows={5}
              disabled={groupIndexPendingEquipment}
            />
            <div className="actions">
              <button
                className="button button-hollow-green"
                type="button"
                onClick={onAddEquipment}
              >
                {titleize(t('ADD'))}
              </button>
            </div>
          </div>
        )}
        {group.selectedEquipment.length > 0 && (
          <div className="col-span-full">
            <hr className="my-6" />
            <h4 className="mb-2">{t('SELECTED_EQUIPMENT')}</h4>
            <Alert type="info" className="mb-4 max-w-lg" canClose>
              {t('MBL_INFO_MESSAGE')}
            </Alert>
            <div className="overflow-x-auto">
              <List title="Equipment">
                {group.selectedEquipment.map((equipment, i) => (
                  <EquipmentRow
                    key={i}
                    equipment={equipment}
                    onRemove={onRemoveEquipment}
                    onChange={onEquipmentFieldChange}
                    onApplyAll={onApplyAll}
                    requiredReferenceNumbers={requiredReferenceNumbers}
                  />
                ))}
              </List>
            </div>
          </div>
        )}
        <div className="block mb-4">
          {!group.hasEquipment ? (
            <div className="">
              <label className="mb-2">{t('REQUIRED_REFERENCE_NUMBERS')}</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {referenceNumbersWithoutEquipment.map((refNo) => {
                  return (
                    <CargomaticInput
                      key={refNo.reference_number}
                      label={`${t(refNo?.reference_number?.toUpperCase())} *`}
                      value={refNo.value}
                      onChange={(value) =>
                        handleReferenceNumberChanges(refNo, value)
                      }
                    />
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className="p-4 shadow rounded border">
      <div className="flex justify-between items-center mb-4 border-b pb-4 -mx-8">
        <div className="flex flex-wrap space-x-1 px-8">
          <h4 className="my-0 flex-1 sm:w-24 w-full">
            {t('GROUP_WITH_VALUE', { value: index + 1 })}
          </h4>
          {!group.consignee && (
            <div className="w-full sm:w-64">
              <label className="mb-1 form-label">{t('SEARCH_CONSIGNEE')}</label>
              <CustomerSearch
                clearOnSelect={false}
                onChange={handleConsigneeFieldChange}
                onSelect={handleConsigneeSelect}
                placeholder={t('SEARCH_FOR_CONSIGNEE')}
                type="consignees"
                authorizedOnly={false}
                value={group.consignee_name}
              />
            </div>
          )}
          {group.consignee && (
            <div>
              <label className="mb-1 form-label">
                {titleize(t('CONSIGNEE', { count: 1 }))}
              </label>
              <Badge color="blue" onRemove={handleRemoveConsignee} withRemove>
                {group.consignee_name}
              </Badge>
            </div>
          )}
        </div>
        <div className="px-8">
          {canRemove && (
            <div className="form-actions">
              <DeleteButton onClick={onRemove} />
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2 divide-y divide-gray-200">
        {group.stops.map((stop, i) => (
          <StopForm
            key={i}
            stop={stop}
            onStopChange={handleStopChange}
            onRemove={handleRemoveStop}
          />
        ))}
        <a href="#" className="block pt-4 underline" onClick={onAddStop}>
          {t('ADD_STOP')}
        </a>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <Checkbox
          className="mb-4"
          name={bookingTypeId}
          id={bookingTypeId}
          checked={group.hasEquipment}
          onChange={handleToggleBookingType}
        >
          {t('HAS_EQUIPMENT')}
        </Checkbox>
        {group.hasEquipment && (
          <div className="col-span-full">
            <CargomaticInput
              className="mb-2"
              type="textarea"
              label={titleize(t('EQUIPMENT'))}
              info={t('ADD_EQUIPMENT_NOTE_WITH_ADD_INFO')}
              value={group.equipment}
              placeholder={t('ADD_EQUIPMENT_NOTE')}
              onChange={handleEquipmentChange}
              rows={5}
              disabled={groupIndexPendingEquipment}
            />
            <div className="actions">
              <button
                className="button button-hollow-green"
                type="button"
                onClick={onAddEquipment}
              >
                {titleize(t('ADD'))}
              </button>
            </div>
          </div>
        )}
        {group.selectedEquipment.length > 0 && (
          <div className="col-span-full">
            <h4 className="mb-2">{t('SELECTED_EQUIPMENT')}</h4>
            <Alert type="info" className="mb-4 max-w-lg" canClose>
              {t('MBL_INFO_MESSAGE')}
            </Alert>
            <div className="overflow-x-auto">
              <table className="default-table">
                <thead>
                  <tr>
                    <th>{titleize(t('EQUIPMENT_ID'))}</th>
                    <th>{t('ASSET_TYPE')}</th>
                    <th>{titleize(t('TYPE'))}</th>
                    <th>{titleize(t('SIZE', { count: 1 }))}</th>
                    <th>{t('MASTER_BOL')}</th>
                    <th>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {group.selectedEquipment.map((equipment, i) => (
                    <EquipmentRow
                      key={i}
                      equipment={equipment}
                      onRemove={onRemoveEquipment}
                      onChange={onEquipmentFieldChange}
                      onApplyAll={onApplyAll}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {!group.hasEquipment && (
          <CargomaticInput
            className="mb-4 sm:w-24 w-auto"
            type="number"
            step={1}
            min={1}
            max={MAX_SHIPMENTS}
            label={t('HOW_MANY_SHIPMENTS')}
            info={t('ADD_EQUIPMENT_NOTE')}
            value={group.count}
            placeholder={t('NUMBER_OF_SHIPMENTS')}
            onChange={handleShipmentCountChange}
          />
        )}
      </div>
    </div>
  );
};

function getInitialState(props) {
  const { origin, destination } = props;
  return {
    groups: [getNewGroup({ origin, destination })],
    enablePortEvents: true,
    carrier: null,
    driver: null,
    isCarrierTrained: true,
    saving: false,
    errors: {},
    success: false,
    meta: {
      carrier: '',
      driver: '',
    },
    carrierPreferences: {
      preferred: [],
      excluded: [],
    },
    drivers: [],
    templates: [],
    templateId: null,
    templateName: '',
    saveAsToggled: false,
    request: {},
    groupIndexPendingEquipment: null,
    requiredReferenceNumbers: [],
    requiredAtBooking: [],
    referenceNumbersWithoutEquipment: [],
    templateFetchPending: true,
    selectedTemplate: null,
  };
}

function applyTemplate(template) {
  const groupsData = template.groups.map((group) => {
    var groupData = omit(group, ['stops', 'consignee']);
    var stopsData = group.stops.map((stop) => {
      var location = [
        { name: stop.location_name, address: stop.location_address },
      ];
      var stopData = assign(omit(stop, ['location_address', 'location_name']), {
        location,
      });

      return getNewStop(stopData);
    });
    const restGroupData = {
      stops: stopsData,
      consignee: group.consignee?._id,
      consignee_name: titleize(group.consignee?.optimized_name),
      selectedEquipment: [],
    };
    return assign(groupData, restGroupData);
  });
  const driverData = assign(template.driver, {
    name: titleize(template.driver?.optimized_name),
  });
  return {
    groups: groupsData,
    enablePortEvents: template.enable_port_events,
    carrier: template.carrier?._id,
    driver: template.driver?._id,
    meta: {
      carrier: template.carrier,
      driver: driverData,
    },
    templateId: template._id,
    templateName: template.name,
  };
}

const ShipperTemplatesDropdown = ({ templates, selectedTemplate, onSelect, isLoading, t }) => {
  if (isLoading) {
    return <LoadingCircle text={t('LOADING')} />;
  }

  if (!templates?.length) {
    return <Alert type="warning">{t('NO_ORDER_TEMPLATE_FOUND_FOR_SHIPPER')}</Alert>;
  }

  return (
    <select
      className="form-select w-64"
      value={selectedTemplate?._id || ''}
      onChange={onSelect}
      data-testid="book-multiple-shipments-template-select"
    >
      <option value="">{t('POPULATE_TEMPLATE')}</option>
      {templates.map((template) => (
        <option 
          value={template._id} 
          key={template._id}
          data-testid={`template-${template._id}`}
        >
          {template.name}
        </option>
      ))}
    </select>
  );
};

class BulkingBookingForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...getInitialState(props),
      selectedTemplate: null,
    };
  }

  async getShipmentTemplatesData(params) {
    const { t } = this.props;
    try {
      const response = await ShipmentWebAPIUtils.getShipmentTemplates(params);
      this.setState({
        templates: response.templates,
        templateFetchPending: false,
      });
    } catch (e) {
      console.log(e);
      showToast(
        t('SHIPMENT'),
        t('FAILED_TO_FETCH_SHIPMENT_TEMPLATES'),
        'error',
      );
    }
  }

  async componentDidMount() {
    const { shipperId } = this.props;
    const limit = SHIPMENT_TEMPLATE_LIMIT;
    this.getShipmentTemplatesData({
      limit,
      shipper_id: shipperId,
      is_active: true,
    });
    this.getCarrierPreferences(shipperId);
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
      const requiredReferenceNumbers =
        await this.getShipperReferenceNumberRequirements(shipperId);
      this.setShipperReferenceNumberRequirements(requiredReferenceNumbers);
    }
  }

  getCarrierPreferences = async (shipperId) => {
    const carrierPreferences = await getCarrierPreferencesByType(
      shipperId,
      'drayage',
    );
    this.setState({ carrierPreferences });
  };

  getShipperReferenceNumberRequirements = async (shipperId) => {
    try {
      const response = await getReferenceNumberRequirements(shipperId);
      const requiredReferenceNumbers = (
        response?.data?.getReferenceNumberRequirementsQuery || []
      ).map((refNo) => {
        return {
          ...refNo,
          reference_number: refNo?.reference_number?.slug,
          value: '',
        };
      });
      return requiredReferenceNumbers;
    } catch (e) {
      console.error(e);
    }
  };

  setShipperReferenceNumberRequirements = (referenceNumbers) => {
    const { order } = this.props;

    if (order) {
      const { order_type } = order;
      const bookingTimeRequirements = referenceNumbers.filter((refNo) => {
        return refNo.required_at === 'BOOKING_TIME';
      });
      const requiredAtBooking = getOrderMasterReferenceBookingTimeRequirement(
        bookingTimeRequirements,
        order_type,
      );
      const allRefNums = featureEnabled(CONFIGURED_FEATURES.SSR)
        ? mapOrderMasterReferenceBookingTimeRequirement(
            requiredAtBooking,
            order_type,
          )
        : [];
      this.setState({
        requiredAtBooking,
        requiredReferenceNumbers: referenceNumbers,
        referenceNumbersWithoutEquipment: allRefNums,
      });
    }
  };

  async getShipmentTemplateData(templateId) {
    try {
      const response =
        await ShipmentWebAPIUtils.getShipmentTemplate(templateId);
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  getCarrierDrivers = async () => {
    const response = await getAuthorizedDrivers(this.state.carrier);

    const {
      entity: { drivers: carrierDrivers },
    } = response;

    return carrierDrivers.map((driver) => {
      const { first_name: firstName, last_name: lastName } = driver;

      const first_name = (firstName || '').trim();
      const last_name = (lastName || '').trim();
      const name = `${first_name} ${last_name}`.trim();

      return {
        ...driver,
        first_name,
        last_name,
        name,
      };
    });
  };

  isSaveDisabled = () => {
    return this.state.saving || this.state.groupIndexPendingEquipment !== null;
  };

  handleTemplateSelect = async (e) => {
    const templateId = e.target.value;
    const { t } = this.props;
    
    try {
      if (!templateId) {
        return this.setState({
          selectedTemplate: null,
          templateName: '',
        });
      }

      const template = await this.getShipmentTemplateData(templateId);
      
      if (template) {
        const modalData = applyTemplate(template);
        this.setState({
          ...modalData,
          selectedTemplate: template,
          templateName: template.name,
        }, async () => {
          if (this.state.carrier) {
            const drivers = await this.getCarrierDrivers();
            this.setState({ drivers });
          }
        });
      } else {
        this.setState({
          selectedTemplate: null,
          templateName: '',
        });
      }
    } catch (e) {
      showToast(t('SHIPMENT'), t('FAILED_TO_FETCH_SHIPMENT_TEMPLATES'), 'error');
    }
  };

  handleTemplateNameChange = (e) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        templateName: e,
      };
    });
  };

  handleToggleSaveAs = () =>
    this.setState({
      saveAsToggled: true,
    });

  handleAddGroupEquipment = (groupIndex) => () => {
    const { equipment } = this.state.groups[groupIndex];
    if (!equipment) {
      return;
    }
    this.setState({ groupIndexPendingEquipment: groupIndex }, async () => {
      const containerMasterBillOfLadingPairs =
        parseContainerMasterBillOfLadingPairs(equipment);
      const equipmentRequested = containerMasterBillOfLadingPairs.map(
        (pair) => pair.container,
      );

      const res = await getAllEquipment({
        select: '_id equipment_id _equipmentType _type _equipmentSize',
        per_page: 200,
        equipment_id: equipmentRequested,
      });
      const {
        entity: { items },
      } = res;

      const eqMap = items.reduce((runningMap, eq) => {
        runningMap[eq.equipment_id] = eq;
        return runningMap;
      }, {});

      const mappedEquipment = equipmentRequested.map((eq) => {
        const mapped = {
          equipment_id: eq,
          ...(eqMap[eq] || {}),
        };

        if (!mapped._id) {
          mapped._equipmentType = 'container';
        }
        return mapped;
      });

      this.setState((state) => {
        const target = cloneDeep(state.groups[groupIndex]);
        target.equipment = '';
        target.selectedEquipment = uniqBy(
          [...target.selectedEquipment, ...mappedEquipment],
          (eq) => eq.equipment_id,
        ).map((eq) => {
          const equipObj = {
            ...eq,
            masterBillOfLading: eq.masterBillOfLading
              ? eq.masterBillOfLading
              : containerMasterBillOfLadingPairs.find(
                  (pair) => pair.container === eq.equipment_id,
                )?.masterBillOfLading,
          };
          state.requiredAtBooking.forEach((refNo) => {
            equipObj[refNo.reference_number] = equipObj?.[
              refNo.reference_number
            ]
              ? equipObj[refNo.reference_number]
              : '';
          });
          return equipObj;
        });

        target.page = 1;
        target.pages = Math.ceil(
          target.selectedEquipment.length / EQUIPMENT_PAGE_SIZE,
        );

        return {
          groupIndexPendingEquipment: null,
          groups: [
            ...state.groups.slice(0, groupIndex),
            target,
            ...state.groups.slice(groupIndex + 1),
          ],
        };
      });
    });
  };

  handleCloseAlert = () => {
    this.setState({ success: false });
  };

  handleCloseErrorAlert = () => {
    this.setState({ errors: {} });
  };

  handleAddGroup = () => {
    const { origin, destination } = this.props;
    this.setState((state) => ({
      groups: [...state.groups, getNewGroup({ origin, destination })],
    }));
  };

  handleRemoveGroup = (index) => () => {
    this.setState((state) => ({
      groups: [
        ...state.groups.slice(0, index),
        ...state.groups.slice(index + 1),
      ],
    }));
  };

  handleAddGroupStop = (groupIndex) => () => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);
      const newSequence =
        (target.stops[target.stops.length - 1]?.sequence || 0) + 1;

      target.stops = [...target.stops, getNewStop({ sequence: newSequence })];

      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleRemoveGroupStop = (groupIndex) => (stop) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);
      const stopIndex = target.stops.findIndex(
        (s) => s.sequence === stop.sequence,
      );
      target.stops = [
        ...target.stops.slice(0, stopIndex),
        ...target.stops.slice(stopIndex + 1),
      ].map((stop, i) => ({ ...stop, sequence: i + 1 }));

      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleGroupConsigneeChange =
    (groupIndex) =>
    ({ consignee_name, consignee }) => {
      this.setState((prevState) => {
        const target = cloneDeep(prevState.groups[groupIndex]);
        target.consignee = consignee;
        target.consignee_name = consignee_name;
        return {
          groups: [
            ...prevState.groups.slice(0, groupIndex),
            target,
            ...prevState.groups.slice(groupIndex + 1),
          ],
        };
      });
    };

  handleRemoveGroupConsignee = (groupIndex) => () => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);
      target.consignee = null;
      target.consignee_name = '';
      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleRemoveGroupEquipment = (groupIndex) => (equipmentId) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);
      const equipmentIndex = target.selectedEquipment.findIndex(
        (eq) => eq.equipment_id === equipmentId,
      );
      target.selectedEquipment = [
        ...target.selectedEquipment.slice(0, equipmentIndex),
        ...target.selectedEquipment.slice(equipmentIndex + 1),
      ];

      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleGroupEquipmentApplyAll = (groupIndex) => (equipmentId) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);
      const equipmentIndex = target.selectedEquipment.findIndex(
        (eq) => eq.equipment_id === equipmentId,
      );

      const targetEquipment = target.selectedEquipment[equipmentIndex];

      target.selectedEquipment = target.selectedEquipment.map((eq, i) => {
        if (eq._id || i === equipmentIndex) {
          return eq;
        }

        let _type = eq._type;
        let _equipmentSize = eq._equipmentSize;
        if (!_type) {
          _type = targetEquipment._type;
        }

        if (!_equipmentSize) {
          _equipmentSize = targetEquipment._equipmentSize;
        }

        return {
          ...eq,
          _type,
          _equipmentSize,
        };
      });

      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleGroupStopChange = (groupIndex) => (stop) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);

      const stopIndex = target.stops.findIndex(
        (s) => s.sequence === stop.sequence,
      );

      target.stops = [
        ...target.stops.slice(0, stopIndex),
        stop,
        ...target.stops.slice(stopIndex + 1),
      ];
      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleGroupEquipmentFieldChange =
    (groupIndex) => (equipmentId, field, value) => {
      this.setState((prevState) => {
        const target = cloneDeep(prevState.groups[groupIndex]);
        const equipmentIndex = target.selectedEquipment.findIndex(
          (eq) => eq.equipment_id === equipmentId,
        );

        const equipment = {
          ...target.selectedEquipment[equipmentIndex],
          [field]: value,
        };

        if (field === '_equipmentType' && value === 'container') {
          equipment._type = null;
          equipment._equipmentSize = null;
        }

        target.selectedEquipment = [
          ...target.selectedEquipment.slice(0, equipmentIndex),
          equipment,
          ...target.selectedEquipment.slice(equipmentIndex + 1),
        ];

        return {
          groups: [
            ...prevState.groups.slice(0, groupIndex),
            target,
            ...prevState.groups.slice(groupIndex + 1),
          ],
        };
      });
    };

  handleGroupFieldChange = (groupIndex) => (field, value) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);
      target[field] = value;

      if (field === 'hasEquipment' && !value) {
        target.equipment = '';
        target.selectedEquipment = [];
        target.count = 1;
      } else if (field === 'hasEquipment' && value) {
        target.count = 0;
      }

      if (field === 'count') {
        target.count = parseInt(value, 10) || 0;
      }

      const groups = [
        ...prevState.groups.slice(0, groupIndex),
        target,
        ...prevState.groups.slice(groupIndex + 1),
      ];

      const updates = {
        groups,
      };
      return updates;
    });
  };

  handleReferenceNumberChange = (refNo, value) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.referenceNumbersWithoutEquipment);
      const index = target.findIndex(
        (ref) => ref.reference_number === refNo.reference_number,
      );
      if (index > -1) {
        target[index].value = value;
      }
      return { ...prevState, referenceNumbersWithoutEquipment: target };
    });
  };

  handleGroupAddressChange = (groupIndex) => (locationIndex, field, value) => {
    this.setState((prevState) => {
      const target = cloneDeep(prevState.groups[groupIndex]);

      const location = {
        ...target.locations[locationIndex],
        [field]: value,
      };

      target.locations = [
        ...target.locations.slice(0, locationIndex),
        location,
        ...target.locations.slice(locationIndex + 1),
      ];

      return {
        groups: [
          ...prevState.groups.slice(0, groupIndex),
          target,
          ...prevState.groups.slice(groupIndex + 1),
        ],
      };
    });
  };

  handleSelectCarrier = (_value, item) => {
    this.setState(
      (prevState) => {
        const { meta } = prevState;
        return {
          carrier: item._id,
          isCarrierTrained: item.status === 'Trained',
          meta: {
            ...meta,
            carrier: item,
            driver: '',
          },
        };
      },
      async () => {
        const drivers = await this.getCarrierDrivers();
        const driverItem = drivers.find(
          (drvr) => drvr._id === item.default_driver,
        );
        this.setState((prevState) => {
          return {
            ...prevState,
            drivers,
            driver: item.default_driver,
            meta: {
              ...prevState.meta,
              driver: driverItem,
            },
          };
        });
      },
    );
  };

  handleSelectDriver = (_value, item) => {
    this.setState(
      (prevState) => {
        const { meta } = prevState;

        const { _id: driver, carrierData: carrier } = item;

        return {
          carrier: carrier._id,
          isCarrierTrained: carrier.status === 'Trained',
          driver,
          meta: {
            ...meta,
            driver: item,
            carrier,
          },
        };
      },
      async () => {
        if (this.state.carrier) {
          const drivers = await this.getCarrierDrivers();
          this.setState({ drivers });
        }
      },
    );
  };

  handleDriverDropdownChange = (e) => {
    const {
      target: { value: driver },
    } = e;
    const item = this.state.drivers.find((drvr) => drvr._id === driver);
    this.setState((prevState) => {
      return {
        ...prevState,
        driver,
        meta: {
          ...prevState.meta,
          driver: item,
        },
      };
    });
  };

  handleRemoveCarrier = () => {
    this.setState((prevState) => {
      const { meta } = prevState;
      return {
        carrier: null,
        driver: null,
        isCarrierTrained: true,
        meta: {
          ...meta,
          carrier: '',
          driver: '',
        },
      };
    });
  };

  handleRemoveDriver = () => {
    this.setState((prevState) => {
      const { meta } = prevState;
      return {
        driver: null,
        meta: {
          ...meta,
          driver: '',
        },
      };
    });
  };

  handleTogglePortEvents = (enablePortEvents) => {
    this.setState({ enablePortEvents });
  };

  handleSave = (updateTemplate = false) => {
    const { t } = this.props;
    this.setState(
      (prevState) => {
        const errors = this.validate(prevState);
        const saving = Object.keys(errors).length === 0;
        return {
          errors,
          saving,
        };
      },
      async () => {
        if (Object.keys(this.state.errors).length > 0) {
          return;
        }

        const newEquipment = uniqBy(
          this.state.groups.reduce((list, job) => {
            if (!job.hasEquipment) {
              return list;
            }

            return list.concat(job.selectedEquipment.filter((eq) => !eq._id));
          }, []),
          (eq) => eq.equipment_id,
        );

        let bulkAddResults;
        let equipment = [];
        try {
          if (newEquipment.length > 0) {
            bulkAddResults = await bulkAddEquipment(newEquipment);
            equipment = bulkAddResults.entity;
          }
        } catch (e) {
          console.log(e);
          const error = e.entity.error;
          if (error.code === 11000) {
            const insertErrors = error.writeErrors.reduce(
              (errorMap, writeError) => {
                if (
                  writeError.code === 11000 &&
                  writeError.errmsg.includes('equipment_id')
                ) {
                  const {
                    op: { equipment_id },
                  } = writeError;
                  errorMap[equipment_id] = t('EQUIPMENT_ALREADY_EXISTS', {
                    equipment_id,
                  });
                  return errorMap;
                }
                return errorMap;
              },
              {},
            );

            if (Object.keys(insertErrors).length > 0) {
              this.setState({ errors: insertErrors, saving: false });
              return;
            }
          }
        }

        const request = this.transformRequest(equipment);
        if (updateTemplate) {
          try {
            const { templateId, templateName } = this.state;
            if (templateId) {
              await ShipmentWebAPIUtils.saveTemplateAndBookShipments(
                this.props.orderId,
                templateId,
                templateName,
                request,
              );
            } else {
              await ShipmentWebAPIUtils.createShipmentsAndTemplate(
                this.props.orderId,
                templateName,
                request,
              );
            }
            const initialState = getInitialState(this.props);
            this.setState({
              ...initialState,
              saving: false,
              success: true,
            });

            this.props.onSaveSuccess();
          } catch (e) {
            console.error(e);
            const errors = {
              summary: t('ERROR_ON_SHIPMENTS_BOOKING_WITH_MESSAGE', {
                message: e.message,
              }),
            };
            showToast(
              titleize(t('SHIPMENT')),
              t('SAVE_TEMPLATE_AND_BOOK_SHIPMENT_ERROR'),
              'error',
            );
            this.setState({ errors, saving: false, success: false });
          }
        } else {
          try {
            await ShipmentWebAPIUtils.bookMultipleOrderShipments(
              this.props.orderId,
              request,
            );
            const initialState = getInitialState(this.props);
            this.setState({
              ...initialState,
              saving: false,
              success: true,
            });

            this.props.onSaveSuccess();
          } catch (e) {
            console.error(e);
            const errors = {
              summary: t('ERROR_ON_SHIPMENTS_BOOKING_WITH_MESSAGE', {
                message: e.message,
              }),
            };

            this.setState({ errors, saving: false, success: false });
          }
        }
      },
    );
  };

  handleCancel = () => {
    this.props.onHide();
  };

  validate(request) {
    const errors = {};

    const { groups } = request;
    const { t } = this.props;
    const flattenedStops = groups.reduce((groupStops, group) => {
      return groupStops.concat(...group.stops);
    }, []);

    if (!this.state.isCarrierTrained) {
      errors.carrier = t('CARRIER_IS_NOT_TRAINED');
    }

    if (
      !flattenedStops.every((stop) => stop.location && stop.location[0].address)
    ) {
      errors.groups = t('PROVIDE_ADDRESS_TO_EACH_STOP');
    }

    const groupsWithEquipment = groups.filter((dest) => dest.hasEquipment);
    if (
      !groupsWithEquipment.every((dest) => dest.selectedEquipment.length > 0)
    ) {
      errors.equipment = t('PROVIDE_LIST_EQUIPMENT_TO_GROUPS');
    } else if (
      !groupsWithEquipment.every((dest) => {
        return dest.selectedEquipment.every(
          (eq) => eq._equipmentType === 'container',
        );
      })
    ) {
      errors.equipment = t('ONLY_CONTAINERS_ALLOWED');
    } else if (
      !groupsWithEquipment.every((dest) => {
        return dest.selectedEquipment
          .filter((eq) => !eq._id)
          .every((eq) => eq._type && eq._equipmentSize);
      })
    ) {
      errors.equipment = t('CONTAINER_MUST_HAVE_TYPE_AND_SIZE');
    }

    const groupsWithCounts = groups.filter((dest) => !dest.hasEquipment);
    if (!groupsWithCounts.every((dest) => dest.count > 0)) {
      errors.count = t('SHIPMENTS_REQUIRED_EQUIPMENT_ERROR');
    }

    const count = groupsWithCounts.reduce((sum, group) => {
      return sum + (group.count || 0);
    }, 0);

    const containerCount = groupsWithEquipment.reduce((sum, group) => {
      return sum + (group.selectedEquipment || []).length;
    }, 0);

    if (count + containerCount > MAX_SHIPMENTS) {
      errors.maxCount = t('MAX_SHIPMENTS_WITH_VALUE', { value: MAX_SHIPMENTS });
    }

    return errors;
  }

  transformRequest(newEquipment) {
    const {
      carrier,
      driver,
      enablePortEvents: enable_port_events,
      groups,
      referenceNumbersWithoutEquipment,
    } = this.state;

    const eqMap = newEquipment.reduce((runningMap, eq) => {
      runningMap[eq.equipment_id] = eq._id;
      return runningMap;
    }, {});

    const refNos = referenceNumbersWithoutEquipment.reduce((reducer, refNo) => {
      reducer[refNo.reference_number] = refNo.value;
      return reducer;
    }, {});

    return {
      shipper: this.props.shipperId,
      carrier,
      driver,
      enable_port_events,
      shipments: groups.map((d) => {
        const { consignee, stops, count: countStr, hasEquipment } = d;
        const count = parseInt(countStr, 10) || 0;
        return {
          consignee,
          equipment: d.selectedEquipment.map((eq) => {
            let id;
            if (eq._id) {
              id = eq._id;
            } else {
              id = eqMap[eq.equipment_id];
            }
            const { masterBillOfLading } = eq;
            const returnObj = {
              _id: id,
              masterBillOfLading,
            };
            this.state.requiredAtBooking.forEach((refNo) => {
              returnObj[refNo.reference_number] = eq[refNo.reference_number];
            });
            return returnObj;
          }),
          count,
          hasEquipment,
          stops,
          referenceNumbers: refNos,
        };
      }),
    };
  }

  renderSuccess() {
    const { success } = this.state;

    if (!success) {
      return null;
    }
    const { t } = this.props;
    return (
      <Alert
        type="success"
        title={t('BOOKING_SUCCESSFUL')}
        canClose
        onClose={this.handleCloseAlert}
      >
        {t('SHIPMENTS_SUCCESSFULLY_BOOKED')}
      </Alert>
    );
  }

  renderErrors() {
    const errorKeys = Object.keys(this.state.errors);

    if (errorKeys.length === 0) {
      return null;
    }
    const { t } = this.props;
    return (
      <div className="mb-4">
        <ErrorAlert
          title={t('ADDRESS_ISSUES')}
          onClose={this.handleCloseErrorAlert}
          canClose
        >
          <ul className="ml-0 list-reset">
            {errorKeys.map((errorKey) => (
              <li key={errorKey}>{this.state.errors[errorKey]}</li>
            ))}
          </ul>
        </ErrorAlert>
      </div>
    );
  }

  renderShipmentTemplateActions() {
    if (!SessionStore.hasOrderTemplateAdminRole()) {
      return null;
    }
    const { t } = this.props;
    const buttons = [];

    if (this.state.selectedTemplate && !this.state.saveAsToggled) {
      buttons.push(
        <div
          className="px-2 mb-4 sm:mb-0"
          key="shipment-save-order-template-button"
        >
          <button
            id="shipment-saveas-button"
            className="block mr-2 button button-hollow-green"
            onClick={this.handleToggleSaveAs}
          >
            {t('SAVE_AS')}
          </button>
        </div>,
      );
    }

    if (!this.state.selectedTemplate || this.state.saveAsToggled) {
      buttons.push(
        <div
          className="w-64 px-2 mb-4 sm:mb-0"
          key="shipment-create-order-template-input"
        >
          <CargomaticInput
            type="text"
            name="template-name"
            className="mb-2"
            placeholder={t('TEMPLATE_NAME')}
            disabled={this.isSaveDisabled()}
            value={this.state.templateName}
            onChange={this.handleTemplateNameChange}
          />
        </div>,
      );
    }

    buttons.push(
      <div
        className="px-2 mb-4 sm:mb-0"
        key="shipment-create-order-template-button"
      >
        <button
          id="shipment-create-order-template-button"
          className="block mr-2 button button-hollow-green"
          disabled={this.isSaveDisabled() || !this.state.templateName}
          onClick={() => {
            this.handleSave(true);
          }}
        >
          {this.state.selectedTemplate && <span>{t('SAVE_AND_BOOK_SHIPMENTS')}</span>}

          {!this.state.selectedTemplate && (
            <span>{t('CREATE_AND_BOOK_SHIPMENTS')}</span>
          )}
        </button>
      </div>,
    );
    return <div className="flex items-center">{buttons}</div>;
  }

  renderShipperTemplatesDropdown = (templates = []) => {
    const { t } = this.props;
    return (
      <ShipperTemplatesDropdown
        templates={templates}
        selectedTemplate={this.state.selectedTemplate}
        onSelect={this.handleTemplateSelect}
        isLoading={this.state.templateFetchPending}
        t={t}
      />
    );
  };

  render() {
    const { t } = this.props;
    const { carrierPreferences, templates } = this.state;

    return (
      <Modal
        show={this.props.show}
        onHide={this.props.onHide}
        dialogClassName="bg-gray-50 modal-override"
      >
        <Modal.Header>
          <div className="flex content-center justify-between">
            <div className="flex items-center">
              <h3
                id="create-shipment-header"
                className="text-grey-darker whitespace-nowrap modal-title mr-4"
              >
                {t('BOOK_MULTIPLE_SHIPMENTS')}
              </h3>
              {this.renderShipperTemplatesDropdown(templates)}
            </div>
            <div className="flex justify-end">
              <a
                className="text-lg font-bold secondary-action hover:no-underline"
                onClick={this.handleCancel}
              >
                &times;
              </a>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div
            className="mb-4 overflow-x-hidden overflow-y-auto"
            style={{ maxHeight: '100vh' }}
          >
            
            {this.renderSuccess()}
            {this.renderErrors()}
            <div className="flex -mx-4">
              <div className="px-4 mb-4">
                <label className="block mb-2 form-label">
                  {t('SELECR_A_CARRIER')}
                </label>
                {!this.state.carrier && (
                  <CustomerSearchWithPreferences
                    clearOnSelect={false}
                    onSelect={this.handleSelectCarrier}
                    placeholder={t('SEARCH_FOR_A_CARRIER')}
                    type="carriers"
                    authorizedOnly
                    carrierPreferences={carrierPreferences}
                    isDriver={false}
                  />
                )}
                {this.state.carrier && (
                  <div className="flex items-start justify-start">
                    <Badge
                      onRemove={this.handleRemoveCarrier}
                      withRemove
                      color="green"
                      text={this.state.meta.carrier.name}
                    />
                  </div>
                )}
              </div>
              <div className="px-4 mb-4">
                <label className="block mb-2 form-label">
                  {titleize(t('SELECT_A_DRIVER'))}
                </label>
                {!this.state.carrier && (
                  <CustomerSearchWithPreferences
                    clearOnSelect={false}
                    onSelect={this.handleSelectDriver}
                    placeholder={t('SEARCH_FOR_A_DRIVER')}
                    type="drivers"
                    authorizedOnly
                    carrierPreferences={carrierPreferences}
                    isDriver
                  />
                )}
                {this.state.carrier &&
                  this.state.driver &&
                  this.state.driver.length && (
                    <div className="flex items-start justify-start">
                      <Badge
                        onRemove={this.handleRemoveDriver}
                        withRemove
                        color="green"
                        text={this.state.meta.driver?.name}
                      />
                    </div>
                  )}
                {this.state.carrier &&
                  this.state.drivers &&
                  !this.state.driver && (
                    <select
                      className="mb-4 form-field"
                      value={this.state.driver}
                      onChange={this.handleDriverDropdownChange}
                    >
                      <option value="">
                        {titleize(t('SELECT_A_DRIVER'))}*
                      </option>
                      {this.state.drivers.map((driver) => (
                        <option key={driver._id} value={driver._id}>
                          {driver.name}
                        </option>
                      ))}
                    </select>
                  )}
              </div>
            </div>
            <div className="my-4">
              <div className="flex items-center space-x-2 mb-4">
                <h3 className="my-0">{titleize(t('GROUP', { count: 2 }))}</h3>
                <AddButton onClick={this.handleAddGroup} />
              </div>
              <div className="space-y-4">
                {this.state.groups.map((group, i) => (
                  <Group
                    key={i}
                    index={i}
                    group={group}
                    onAddressChange={this.handleGroupAddressChange(i)}
                    onFieldChange={this.handleGroupFieldChange(i)}
                    onConsigneeChange={this.handleGroupConsigneeChange(i)}
                    onRemoveConsignee={this.handleRemoveGroupConsignee(i)}
                    onStopChange={this.handleGroupStopChange(i)}
                    onApplyAll={this.handleGroupEquipmentApplyAll(i)}
                    onAddEquipment={this.handleAddGroupEquipment(i)}
                    onAddStop={this.handleAddGroupStop(i)}
                    onRemoveStop={this.handleRemoveGroupStop(i)}
                    onRemoveEquipment={this.handleRemoveGroupEquipment(i)}
                    onEquipmentFieldChange={this.handleGroupEquipmentFieldChange(
                      i,
                    )}
                    onRemove={this.handleRemoveGroup(i)}
                    canRemove={i !== 0}
                    equipmentEditingDisabled={
                      this.state.groupIndexPendingEquipment &&
                      this.state.groupIndexPendingEquipment !== i
                    }
                    requiredReferenceNumbers={this.state.requiredAtBooking}
                    referenceNumbersWithoutEquipment={
                      this.state.referenceNumbersWithoutEquipment
                    }
                    onReferenceNumberChange={this.handleReferenceNumberChange}
                  />
                ))}
              </div>
            </div>
            <Checkbox
              checked={this.state.enablePortEvents}
              onChange={this.handleTogglePortEvents}
              className="my-4"
              id="port_events"
              name="port_events"
            >
              {t('ENABLE_PORT_INTEGRATION')}
            </Checkbox>
            <div className="mt-6 form-actions">
              <div className="flex justify-end items-center">
                {this.renderShipmentTemplateActions()}
                <button
                  type="button"
                  onClick={() => {
                    this.handleSave(false);
                  }}
                  disabled={this.isSaveDisabled()}
                  className="block mr-2 button button-green"
                >
                  {this.state.saving && <LoadingCircle text={t('BOOKING')} />}
                  {!this.state.saving && <span>{t('BOOK_SHIPMENTS')}</span>}
                </button>
                <button
                  type="button"
                  onClick={this.handleCancel}
                  disabled={this.isSaveDisabled()}
                  className="button button-secondary"
                >
                  {titleize(t('CANCEL'))}
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
}

function convertStringToArray(value) {
  if (!value) {
    return [];
  }

  return value.split('\n');
}

function parseContainerMasterBillOfLadingPairs(value) {
  const rows = convertStringToArray(value);

  return rows
    .map((str) => {
      const items = str
        .replace(',', '\t')
        .split('\t')
        .map((item) => item?.trim());

      return {
        container: items?.[0]?.trim() || '',
        masterBillOfLading: items?.[1]?.trim() || '',
      };
    })
    .filter((pair) => pair.container);
}

BulkingBookingForm.propTypes = {
  orderId: PropTypes.string.isRequired,
  onHide: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  shipperId: PropTypes.string.isRequired,
};

export default withTranslation()(BulkingBookingForm);
